import { useEffect } from "react";
import { FAQS, CONTACT_EMAIL } from "@/lib/constants";

const TITLE =
  "Heritage Table Catering | Orlando & Central Florida Catering & Handcrafted Desserts";
const DESC =
  "Heritage Table Catering provides multicultural catering, homestyle meals, handcrafted desserts, and event food for families, churches, weddings, and gatherings across Orlando and Central Florida.";
const KEYWORDS = [
  "Orlando catering",
  "Central Florida catering",
  "Orlando wedding catering",
  "church catering Orlando",
  "church catering Central Florida",
  "wedding catering Central Florida",
  "multicultural catering Orlando",
  "homestyle catering Central Florida",
  "handcrafted desserts Central Florida",
  "custom cake catering Central Florida",
  "family event catering Orlando",
  "Hebraic inspired catering",
  "Biblical food catering",
].join(", ");

function setMeta(name, content, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setJSONLD(id, json) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(json);
}

export default function SEO() {
  useEffect(() => {
    document.title = TITLE;
    setMeta("description", DESC);
    setMeta("keywords", KEYWORDS);
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESC, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:locale", "en_US", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", TITLE);
    setMeta("twitter:description", DESC);

    const business = {
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "FoodEstablishment", "CateringService"],
      name: "Heritage Table Catering",
      description: DESC,
      areaServed: [
        { "@type": "City", name: "Orlando" },
        { "@type": "AdministrativeArea", name: "Central Florida" },
      ],
      email: CONTACT_EMAIL,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Catering Inquiries",
        email: CONTACT_EMAIL,
        areaServed: "US-FL",
        availableLanguage: ["English"],
      },
      servesCuisine: [
        "Latin",
        "Caribbean",
        "Cuban",
        "Puerto Rican",
        "Italian",
        "English",
        "Amish-inspired",
        "Mennonite-inspired",
        "Ethiopian-inspired",
        "Southern",
        "American",
        "Biblical / Hebraic-inspired",
      ],
      makesOffer: [
        { "@type": "Offer", name: "Wedding catering" },
        { "@type": "Offer", name: "Church gathering catering" },
        { "@type": "Offer", name: "Multicultural catering" },
        { "@type": "Offer", name: "Handcrafted dessert tables" },
      ],
      brand: { "@type": "Brand", name: "Heritage Table Catering" },
      slogan: "Traditional flavors. Honest food. Beautifully served.",
    };

    const org = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Heritage Table Catering",
      url: typeof window !== "undefined" ? window.location.origin : "",
      sameAs: [
        "https://centralfloridacustomcakes.com/",
        "https://garcesbrosstudios.com/",
        "https://buildyoursourcecode.com/",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Catering Inquiries",
        email: CONTACT_EMAIL,
      },
    };

    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item:
            typeof window !== "undefined" ? window.location.origin + "/" : "/",
        },
      ],
    };

    setJSONLD("ld-business", business);
    setJSONLD("ld-org", org);
    setJSONLD("ld-faq", faqLd);
    setJSONLD("ld-breadcrumb", breadcrumb);
  }, []);

  return null;
}
